// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/lib/inputFormats.ts — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
/**
 * inputFormats — shared as-you-type input formatting + validation core.
 *
 * Canonical: shared/lib/inputFormats.ts — generated copies are synced into all
 * three apps by scripts/sync_shared_code.sh (never hand-edit a copy).
 * No JSX, no imports, no framework dependency — this lib stays React-free.
 *
 * Formats: phone_na, postal_ca, date_iso, month_year, health_card_ns, email.
 * `month_year` is the day-less sibling of `date_iso`, for a box the carrier
 * labels "(mm/yyyy)": it stores the ISO year-month ("2026-09") and the PDF
 * render prints it in the carrier's order (case imp_d97d43c3). Storage stays
 * ISO on both, and only the print localizes.
 * Values are STORED in display form ("(902) 555-1234", "B3H 1A1",
 * "1974-06-21", "1234 567 890") — PDFs/faxes print them as-is; the backend
 * strips punctuation before SMS/E.164 use.
 *
 * Known limitation: forward-delete (Del) on a separator char removes the
 * significant char to its LEFT (same as backspace) — the change event alone
 * cannot distinguish deletion direction.
 */

const digitsOnly = (s: unknown): string => String(s == null ? "" : s).replace(/\D+/g, "");
const alnumOnly = (s: unknown): string => String(s == null ? "" : s).replace(/[^A-Za-z0-9]+/g, "");

/* ---------------------------------------------------------------- phone_na */

function formatPhone(raw: unknown): string {
  let d = digitsOnly(raw);
  if (d.length === 11 && d.charAt(0) === "1") d = d.slice(1);
  d = d.slice(0, 10);
  if (!d) return "";
  if (d.length <= 3) return "(" + d;
  if (d.length <= 6) return "(" + d.slice(0, 3) + ") " + d.slice(3);
  return "(" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6);
}

/* --------------------------------------------------------------- postal_ca */

function formatPostal(raw: unknown): string {
  const chars = String(raw == null ? "" : raw).toUpperCase().replace(/[^A-Z0-9]+/g, "");
  let out = "";
  for (let i = 0; i < chars.length && out.length < 6; i++) {
    const ch = chars.charAt(i);
    const wantLetter = out.length % 2 === 0; // slots: L D L D L D
    if (wantLetter ? /[A-Z]/.test(ch) : /[0-9]/.test(ch)) out += ch;
    // otherwise drop the char (forgiving: never clear the field)
  }
  return out.length > 3 ? out.slice(0, 3) + " " + out.slice(3) : out;
}

/* ---------------------------------------------------------------- date_iso */

/** A complete year-LAST date as a carrier prints it: dd/mm/yyyy, mm/dd/yyyy
 *  or mm/yyyy (separators / - .). Year-first strings are not matched, so a
 *  pasted 1974/06/21 still masks to ISO. */
const CARRIER_SHAPED_DATE_RE = /^\d{1,2}[/.\-](?:\d{1,2}[/.\-])?\d{4}$/;

function isCarrierShapedDate(s: string): boolean {
  return CARRIER_SHAPED_DATE_RE.test(s.trim());
}

function formatDateIso(raw: unknown): string {
  // Imported text in a date box ("Present", "6 months, ongoing") must stay
  // VISIBLE. Digit-stripping it rendered the box empty on screen while the
  // sent PDF printed the stored words — and the physician's typed letters
  // vanished the same way, which read as "cannot edit this field"
  // (imp_e3b0176d). Anything containing a letter is not a masked date entry:
  // show it raw; isFormatValid still flags it invalid (coral wash).
  const s = String(raw == null ? "" : raw);
  if (/[A-Za-z]/.test(s)) return s;
  // A COMPLETE carrier-format date (dd/mm/yyyy, mm/dd/yyyy, mm/yyyy — year
  // last) is a stored value some writer put there in the wrong convention,
  // not a masked entry. Re-masking its digits as if they were ISO turned
  // 07/09/2026 into 0709-02-02 (imp_c17c7c6e). Show it raw; isFormatValid
  // flags it (coral), so the bad write is visible instead of scrambled. A
  // typist can only hit this by typing a full year-last date, which the
  // coral then rejects. Year-first pastes (1974/06/21) still mask to ISO.
  if (isCarrierShapedDate(s)) return s;
  let d = digitsOnly(raw);
  // month auto-pad: first month digit 2-9 can only mean 02-09
  if (d.length >= 5 && d.charAt(4) > "1") d = d.slice(0, 4) + "0" + d.slice(4);
  // month clamp once complete
  if (d.length >= 6) {
    let mm = d.slice(4, 6);
    if (mm === "00") mm = "01";
    else if (mm > "12") mm = "12";
    d = d.slice(0, 4) + mm + d.slice(6);
  }
  // day auto-pad: first day digit 4-9 can only mean 04-09
  if (d.length >= 7 && d.charAt(6) > "3") d = d.slice(0, 6) + "0" + d.slice(6);
  d = d.slice(0, 8);
  // day clamp once complete
  if (d.length >= 8) {
    let dd = d.slice(6, 8);
    if (dd === "00") dd = "01";
    else if (dd > "31") dd = "31";
    d = d.slice(0, 6) + dd;
  }
  if (!d) return "";
  let out = d.slice(0, 4);
  if (d.length >= 4) out += "-";
  out += d.slice(4, 6);
  if (d.length >= 6) out += "-";
  out += d.slice(6, 8);
  return out;
}

function isValidDateIso(s: unknown): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s == null ? "" : s));
  if (!m) return false;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const da = parseInt(m[3], 10);
  if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || da < 1) return false;
  const daysInMonth = new Date(y, mo, 0).getDate();
  return da <= daysInMonth;
}

/* -------------------------------------------------------------- month_year */

/**
 * A month box, day-less. Same digit-mask discipline as date_iso (letters pass
 * through visibly, imp_e3b0176d; month auto-pad and clamp) but it stops at
 * YYYY-MM, because a carrier box labelled "(mm/yyyy)" has no day to ask for
 * and date_iso forced the typist to invent one.
 */
function formatMonthYear(raw: unknown): string {
  const s = String(raw == null ? "" : raw);
  if (/[A-Za-z]/.test(s)) return s;
  // Same rule as date_iso: a stored 09/2026 (or 07/09/2026) is shown raw and
  // flagged, never re-masked into 0920-26 (imp_c17c7c6e).
  if (isCarrierShapedDate(s)) return s;
  let d = digitsOnly(raw);
  // month auto-pad: first month digit 2-9 can only mean 02-09
  if (d.length >= 5 && d.charAt(4) > "1") d = d.slice(0, 4) + "0" + d.slice(4);
  // month clamp once complete
  if (d.length >= 6) {
    let mm = d.slice(4, 6);
    if (mm === "00") mm = "01";
    else if (mm > "12") mm = "12";
    d = d.slice(0, 4) + mm;
  }
  d = d.slice(0, 6);
  if (!d) return "";
  let out = d.slice(0, 4);
  if (d.length >= 4) out += "-";
  out += d.slice(4, 6);
  return out;
}

function isValidMonthYear(s: unknown): boolean {
  const m = /^(\d{4})-(\d{2})$/.exec(String(s == null ? "" : s));
  if (!m) return false;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  return y >= 1900 && y <= 2100 && mo >= 1 && mo <= 12;
}

/* ---------------------------------------------------------- health_card_ns */

function formatHcn(raw: unknown): string {
  const d = digitsOnly(raw).slice(0, 10);
  let out = d.slice(0, 4);
  if (d.length > 4) out += " " + d.slice(4, 7);
  if (d.length > 7) out += " " + d.slice(7);
  return out;
}

/* ------------------------------------------------------------------- email */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatEmail(raw: unknown): string {
  return String(raw == null ? "" : raw).replace(/\s+/g, "");
}

export const COMMON_EMAIL_DOMAINS: string[] = [
  "gmail.com", "hotmail.com", "hotmail.ca", "outlook.com", "outlook.ca",
  "yahoo.com", "yahoo.ca", "icloud.com", "live.com", "live.ca", "aol.com",
  "me.com", "msn.com", "protonmail.com", "proton.me",
  // Common NS / Atlantic Canada ISPs
  "eastlink.ca", "bellaliant.net", "ns.sympatico.ca", "sympatico.ca",
  "accesswave.ca", "xplornet.com", "bell.net", "telus.net", "shaw.ca",
  "rogers.com",
];

function boundedLevenshtein(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev: number[] = [];
  let cur: number[] = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    const t = prev; prev = cur; cur = t;
  }
  return prev[b.length];
}

/**
 * "jane@gmali.com" -> "jane@gmail.com"; returns null when the domain is an
 * exact known domain, is not a near-miss (distance > 2), or the input has no
 * usable local@domain shape. Never blocks — render as a tappable suggestion.
 */
export function suggestDomainTypo(email: unknown): string | null {
  const s = String(email == null ? "" : email).trim();
  const at = s.lastIndexOf("@");
  if (at <= 0 || at === s.length - 1) return null;
  const local = s.slice(0, at);
  const domain = s.slice(at + 1).toLowerCase();
  if (COMMON_EMAIL_DOMAINS.indexOf(domain) !== -1) return null;
  let best: string | null = null;
  let bestDist = 3;
  for (let i = 0; i < COMMON_EMAIL_DOMAINS.length; i++) {
    const dist = boundedLevenshtein(domain, COMMON_EMAIL_DOMAINS[i], 2);
    if (dist < bestDist) { bestDist = dist; best = COMMON_EMAIL_DOMAINS[i]; }
  }
  return best ? local + "@" + best : null;
}

/* ---------------------------------------------------------------- registry */

export interface FormatSpec {
  format: (raw: unknown) => string;
  unformat: (s: unknown) => string;
  isComplete: (s: unknown) => boolean;
  isValid: (s: unknown) => boolean;
  placeholder: string;
  // The 8 HTML inputmode tokens (matches React.HTMLAttributes['inputMode']);
  // kept as a local union so this lib stays React-free.
  inputMode: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
  maxLength: number;
  autoComplete: string;
  message: string;
}

export const FORMATS: Record<string, FormatSpec> = {
  phone_na: {
    format: formatPhone,
    unformat: digitsOnly,
    isComplete: (s) => digitsOnly(s).length === 10,
    isValid: (s) => digitsOnly(s).length === 10,
    placeholder: "(902) 555-1234",
    inputMode: "tel",
    maxLength: 14,
    autoComplete: "tel",
    message: "Enter a 10-digit phone number",
  },
  postal_ca: {
    format: formatPostal,
    unformat: (s) => alnumOnly(s).toUpperCase(),
    isComplete: (s) => /^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(String(s == null ? "" : s)),
    isValid: (s) => /^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(String(s == null ? "" : s)),
    placeholder: "B3H 1A1",
    inputMode: "text",
    maxLength: 7,
    autoComplete: "postal-code",
    message: "Postal code format is A1A 1A1",
  },
  date_iso: {
    format: formatDateIso,
    unformat: (s) => String(s == null ? "" : s),
    isComplete: isValidDateIso,
    isValid: isValidDateIso,
    placeholder: "YYYY-MM-DD",
    inputMode: "numeric",
    maxLength: 10,
    autoComplete: "bday",
    message: "Enter a real date as YYYY-MM-DD",
  },
  month_year: {
    format: formatMonthYear,
    unformat: (s) => String(s == null ? "" : s),
    isComplete: isValidMonthYear,
    isValid: isValidMonthYear,
    placeholder: "YYYY-MM",
    inputMode: "numeric",
    maxLength: 7,
    autoComplete: "off",
    message: "Enter a month as YYYY-MM",
  },
  health_card_ns: {
    format: formatHcn,
    unformat: digitsOnly,
    isComplete: (s) => digitsOnly(s).length === 10,
    isValid: (s) => digitsOnly(s).length === 10,
    placeholder: "1234 567 890",
    inputMode: "numeric",
    maxLength: 12,
    autoComplete: "off",
    message: "MSI health card numbers have 10 digits",
  },
  email: {
    format: formatEmail,
    unformat: (s) => String(s == null ? "" : s).trim(),
    isComplete: (s) => EMAIL_RE.test(String(s == null ? "" : s)),
    isValid: (s) => EMAIL_RE.test(String(s == null ? "" : s)),
    placeholder: "you@example.com",
    inputMode: "email",
    maxLength: 254,
    autoComplete: "email",
    message: "Enter a valid email address",
  },
};

export const FORMAT_KEYS: string[] = Object.keys(FORMATS);

/** Chars that count as "content" (vs separators) per format — caret math. */
const SIGNIFICANT: Record<string, RegExp> = {
  phone_na: /[0-9]/,
  date_iso: /[0-9]/,
  month_year: /[0-9]/,
  health_card_ns: /[0-9]/,
  postal_ca: /[A-Za-z0-9]/,
  email: /\S/,
};

/* ------------------------------------------------------- caret-safe format */

export interface CaretElLike {
  selectionStart?: number | null;
}

export interface ApplyFormatResult {
  next: string;
  nextCaret: number | null;
}

/**
 * Reformat a raw input value while computing where the caret should land.
 * Counts significant chars left of the caret in the raw value, reformats,
 * then re-places the caret after the Nth significant char (skipping past any
 * separators that immediately follow, so typing flows into the next segment).
 *
 * Backspace-over-separator guard: when the edit removed ONLY a separator
 * (e.g. deleting the "-" in "1974-"), also remove the significant char to its
 * left — otherwise the formatter would immediately re-insert the separator
 * and the deletion would appear to do nothing.
 *
 * inputEl only needs { selectionStart } — pass the event target, or a plain
 * object in tests. Returns { next, nextCaret }.
 */
export function applyFormat(
  fmtKey: string,
  inputEl: CaretElLike | null | undefined,
  prevDisplay: unknown,
  rawValue: unknown
): ApplyFormatResult {
  const fmt = FORMATS[fmtKey];
  const raw = String(rawValue == null ? "" : rawValue);
  if (!fmt) return { next: raw, nextCaret: null };
  const sig = SIGNIFICANT[fmtKey] || /[A-Za-z0-9]/;
  const prev = String(prevDisplay == null ? "" : prevDisplay);

  let caret = raw.length;
  if (inputEl && typeof inputEl.selectionStart === "number") caret = inputEl.selectionStart;
  if (caret > raw.length) caret = raw.length;

  let effectiveRaw = raw;
  if (
    prev &&
    raw.length === prev.length - 1 &&
    caret >= 0 && caret < prev.length &&
    prev.slice(0, caret) + prev.slice(caret + 1) === raw &&
    !sig.test(prev.charAt(caret))
  ) {
    let i = caret - 1;
    while (i >= 0 && !sig.test(raw.charAt(i))) i--;
    if (i >= 0) {
      effectiveRaw = raw.slice(0, i) + raw.slice(i + 1);
      caret = i;
    }
  }

  let count = 0;
  for (let i = 0; i < caret; i++) {
    if (sig.test(effectiveRaw.charAt(i))) count++;
  }

  const next = fmt.format(effectiveRaw);

  let nextCaret = 0;
  if (count > 0) {
    let seen = 0;
    nextCaret = next.length;
    for (let j = 0; j < next.length; j++) {
      if (sig.test(next.charAt(j))) {
        seen++;
        if (seen === count) { nextCaret = j + 1; break; }
      }
    }
    // Skip past separators so the caret sits where the next char will go
    while (nextCaret < next.length && !sig.test(next.charAt(nextCaret))) nextCaret++;
    if (nextCaret === next.length && seen === count) {
      // caret at end: fine — trailing separators (e.g. "1974-") are typed over
      nextCaret = next.length;
    }
  }

  return { next, nextCaret };
}

/**
 * One-line onChange wiring for a controlled input:
 *   onChange={(e) => maskedChange("phone_na", value, e, onChange)}
 * Commits the formatted value, force-syncs the DOM (protects against React
 * state-bailout when a char was dropped), and restores the caret after React
 * re-renders. Returns the formatted value.
 */
export function maskedChange(
  fmtKey: string,
  prevValue: unknown,
  event: { target?: any } | null | undefined,
  commit: (value: string) => void
): string {
  const el = event && event.target ? event.target : null;
  const raw = el ? el.value : "";
  if (!FORMATS[fmtKey]) { commit(raw); return raw; }
  const { next, nextCaret } = applyFormat(fmtKey, el, prevValue, raw);
  commit(next);
  if (el && next !== raw) {
    // Controlled-input guard: if state doesn't change (dropped char), React
    // may bail out of re-rendering and the DOM would keep the raw text.
    try { el.value = next; } catch (err) { /* read-only inputs */ }
  }
  if (
    el && nextCaret != null && next !== raw &&
    typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
  ) {
    window.requestAnimationFrame(() => {
      if (el.isConnected !== false && typeof document !== "undefined" &&
          document.activeElement === el && typeof el.setSelectionRange === "function") {
        try { el.setSelectionRange(nextCaret, nextCaret); } catch (err) { /* email/number inputs lack selection API */ }
      }
    });
  }
  return next;
}

/* -------------------------------------------------------------- validation */

/** Run a stored/loaded value through the display formatter (no-op if no fmt). */
export function formatValue(fmtKey: string | null | undefined, value: unknown): unknown {
  const fmt = fmtKey ? FORMATS[fmtKey] : undefined;
  return fmt ? fmt.format(String(value == null ? "" : value)) : value;
}

/** Format validity only — empty values are always "valid" (required-ness is separate). */
export function isFormatValid(fmtKey: string | null | undefined, value: unknown): boolean {
  const fmt = fmtKey ? FORMATS[fmtKey] : undefined;
  if (!fmt) return true;
  const s = String(value == null ? "" : value).trim();
  if (!s) return true;
  return fmt.isValid(s);
}

/** Human message for an invalid non-empty value, else null. */
export function validationMessage(fmtKey: string | null | undefined, value: unknown): string | null {
  if (isFormatValid(fmtKey, value)) return null;
  return fmtKey && FORMATS[fmtKey] ? FORMATS[fmtKey].message : null;
}

/* --------------------------------------------------------------- inference */

const NON_TEXT_TYPES: Record<string, number> = {
  checkbox: 1, radio: 1, signature: 1, select: 1, textarea: 1, large_text: 1,
  toggle: 1, choice: 1,
};

/**
 * Deny guard — names that contradict every maskable format: combined fields
 * (name_and_dob), phone extensions, and content head-nouns (*_drug_name,
 * *_reason, *_pct) including extraction-mistyped date/tel fields. A wrong
 * mask actively blocks typing; a missing mask is harmless. Mirrors backend
 * input_format_binding.py step 1.5 — keep in sync. NOTE: "value" is
 * deliberately NOT denied ("*_value" is a legit dictionary-member convention).
 */
const DENY_FINAL_TOKENS: Record<string, number> = {
  name: 1, drug: 1, medication: 1, company: 1, insurer: 1, reason: 1,
  note: 1, notes: 1, describe: 1, description: 1, comment: 1, comments: 1,
  pct: 1, percent: 1, score: 1, level: 1, result: 1, dose: 1, units: 1,
};

function isDeniedName(mn: string): boolean {
  if (!mn) return false;
  if (/(^|_)and(_|$)|(^|_)ext(ension)?s?(_|$)/.test(mn)) return true;
  const tokens = mn.split("_").filter(Boolean);
  while (tokens.length && /^\d+$/.test(tokens[tokens.length - 1])) tokens.pop();
  if (tokens.length && DENY_FINAL_TOKENS[tokens[tokens.length - 1]]) return true;
  // "X name / phone" or "name / date" combined table columns
  if (/(^|_)name(_|$)/.test(mn) && /phone|fax|(^|_)dob(_|$)|date/.test(mn)) return true;
  return false;
}

/**
 * Infer the input format for a form field / bridge question. Prefers explicit
 * backend-assigned `input_format`; falls back to clinical_concept →
 * dictionary_group → machine_name patterns → type/widget. Returns a FORMATS
 * key or null. Mirrors backend input_format_binding.py — keep in sync.
 */
export function inferInputFormat(field: any): string | null {
  if (!field || typeof field !== "object") return null;
  if (field.input_format && FORMATS[field.input_format]) return field.input_format;

  const t = String(field.type || field.widget || "").toLowerCase();
  if (NON_TEXT_TYPES[t]) return null;

  const mn = String(field.machine_name || field.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");

  if (isDeniedName(mn)) return null;

  const cc = String(field.clinical_concept || "").toLowerCase();
  if (cc) {
    if (/dob|date_of_birth/.test(cc)) return "date_iso";
    if (/health_card|hcn|phn|jhn/.test(cc)) return "health_card_ns";
    if (/postal/.test(cc)) return "postal_ca";
    if (/phone|fax/.test(cc)) return "phone_na";
    if (/email/.test(cc)) return "email";
  }

  const dg = String(field.dictionary_group || "").toLowerCase();
  if (dg === "demo_patient_dob") return "date_iso";
  if (dg === "demo_patient_jhn") return "health_card_ns";
  if (dg === "demo_patient_contact") {
    if (/email/.test(mn)) return "email";
    if (/phone|telephone|(^|_)tel(_|$)|(^|_)cell(_|$)|mobile|fax/.test(mn)) return "phone_na";
  }

  if (/postal/.test(mn)) return "postal_ca";
  if (/(^|_)(hcn|phn|jhn)(_|$)|health_card|healthcard|health_number|msi_number/.test(mn)) return "health_card_ns";
  if (/phone|telephone|(^|_)tel(_|$)|(^|_)cell(_|$)|mobile|fax/.test(mn)) return "phone_na";
  if (/email|e_mail/.test(mn)) return "email";
  if (/date_of_birth|(^|_)dob(_|$)|birth_date|birthdate/.test(mn)) return "date_iso";
  if (/(^|_)date(_|$)/.test(mn)) return "date_iso";

  if (t === "date") return "date_iso";
  if (t === "tel" || t === "telephone") return "phone_na";
  if (t === "email") return "email";
  return null;
}
